import { useEffect, useState } from "react";
import { Warning } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * "You're bidding this lane a lot" nudge.
 *
 * Fired when the pilot has bid the same broker+lane 3+ times in the last
 * 7 days with zero wins and ≥1 known loss (either manually marked or
 * auto-closed by the reaper). Suggests they adjust their ask before
 * firing yet another bid into the same silence.
 *
 * Hides itself when no repeat pattern is detected — silent 99% of the time.
 */
export default function RepeatBidsAlert({ brokerCompany, originState, destinationState }) {
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!brokerCompany) return;
    const params = new URLSearchParams({ broker_company: brokerCompany });
    if (originState) params.append("origin_state", originState);
    if (destinationState) params.append("destination_state", destinationState);

    client.get(`/loadboard/repeat-bids-check?${params.toString()}`)
      .then((r) => alive && setAlert(r.data))
      .catch(() => alive && setAlert(null));
    return () => { alive = false; };
  }, [brokerCompany, originState, destinationState]);

  if (!alert || !alert.alert) return null;

  const laneLabel = alert.origin_state && alert.destination_state
    ? `${alert.origin_state} → ${alert.destination_state}`
    : "this lane";

  return (
    <div
      className="mx-5 my-3 rounded-lg border border-amber-400/40 bg-amber-500/[0.08] p-3 flex items-start gap-3"
      data-testid="repeat-bids-alert"
    >
      <Warning size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
      <div className="text-xs leading-relaxed flex-1 min-w-0">
        <div className="text-amber-200 font-medium">
          You're bidding <b>{alert.broker_company}</b> on {laneLabel} a lot.
        </div>
        <div className="text-neutral-300 mt-1">
          <b className="text-white">{alert.count} bids</b> in the last{" "}
          {alert.days_window} days · <b className="text-white">0 won</b>
          {alert.lost > 0 && <> · <b className="text-white">{alert.lost} lost/auto-closed</b></>}
          {alert.pending > 0 && <> · <b className="text-white">{alert.pending} still open</b></>}.
        </div>
        <div className="text-amber-300/80 mt-1.5 text-[11px]">
          Consider lowering your ask, moving on to a different broker, or waiting for
          a fresher posting on this corridor.
        </div>
      </div>
    </div>
  );
}
